import type { ReactNode } from 'react';

import { Card, Icon, InfoBlock, Text } from '~/components';
import { clsx } from '~/styles/utils';

import {
  amountRowStyle,
  amountTotalStyle,
  breakdownCardStyle,
  breakdownGroupLabelStyle,
  breakdownGroupStyle,
  eyebrowStyle,
  headerTopStyle,
  highlightStyle,
  highlightValueStyle,
  overviewStyle,
  rowLabelStyle,
  rowLabelWithInfotipStyle,
  rowsStyle,
  rowStyle,
  tagStyle,
  titleStyle,
} from './InvoiceSplit.css';

type RootProps = {
  children: ReactNode;
};

export const InvoiceSplitCardRoot = ({ children }: RootProps) => {
  return <Card.Group>{children}</Card.Group>;
};

type OverviewProps = {
  children: ReactNode;
};

export const InvoiceSplitCardOverview = ({ children }: OverviewProps) => {
  return (
    <Card.Root className={overviewStyle} size="small" variant="primary-light">
      {children}
    </Card.Root>
  );
};

type HeaderProps = {
  children: ReactNode;
  tag?: ReactNode;
};

export const InvoiceSplitCardHeader = ({ children, tag }: HeaderProps) => {
  return (
    <div className={headerTopStyle}>
      <p className={eyebrowStyle}>{children}</p>

      {tag && <div className={tagStyle}>{tag}</div>}
    </div>
  );
};

type TitleProps = {
  from: string;
  to: string;
};

export const InvoiceSplitCardTitle = ({ from, to }: TitleProps) => {
  return (
    <p className={titleStyle}>
      <span>{from}</span>
      <Icon name="ArrowRight" size={20} />
      <span>{to}</span>
    </p>
  );
};

type DescriptionProps = {
  children: ReactNode;
};

export const InvoiceSplitCardDescription = ({ children }: DescriptionProps) => {
  return <Text>{children}</Text>;
};

type HighlightProps = {
  children: ReactNode;
  label: string;
};

export const InvoiceSplitCardHighlight = ({ children, label }: HighlightProps) => {
  return (
    <InfoBlock className={highlightStyle} label={label} variant="caps">
      <Text className={highlightValueStyle}>{children}</Text>
    </InfoBlock>
  );
};

type RowsProps = {
  children: ReactNode;
};

export const InvoiceSplitCardRows = ({ children }: RowsProps) => {
  return <div className={rowsStyle}>{children}</div>;
};

type RowProps = {
  children: ReactNode;
  infotip?: ReactNode;
  label: string;
};

export const InvoiceSplitCardRow = ({ children, infotip, label }: RowProps) => {
  return (
    <div className={rowStyle}>
      <span className={rowLabelWithInfotipStyle}>
        <span className={rowLabelStyle}>{label}</span>

        {infotip}
      </span>

      <span>{children}</span>
    </div>
  );
};

type BreakdownProps = {
  children: ReactNode;
};

export const InvoiceSplitCardBreakdown = ({ children }: BreakdownProps) => {
  return (
    <Card.Root size="small">
      <div className={breakdownCardStyle}>{children}</div>
    </Card.Root>
  );
};

type AmountGroupProps = {
  children: ReactNode;
  label?: string;
};

export const InvoiceSplitCardAmountGroup = ({ children, label }: AmountGroupProps) => {
  return (
    <div className={breakdownGroupStyle}>
      {label && <p className={breakdownGroupLabelStyle}>{label}</p>}

      {children}
    </div>
  );
};

type AmountProps = {
  children: ReactNode;
  isTotal?: boolean;
  label: string;
};

export const InvoiceSplitCardAmount = ({ children, isTotal = false, label }: AmountProps) => {
  return (
    <div className={clsx(amountRowStyle, isTotal && amountTotalStyle)}>
      <span>{label}</span>

      <span>{children}</span>
    </div>
  );
};
